import { FC, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useHistory, useParams } from 'react-router-dom'
import { debounce } from 'lodash'
import { useApolloClient } from '@apollo/client'
import {
  LessonAbout,
  LessonButton,
  LessonResourceList,
  LessonSidebar,
  LessonSidebarExpand,
  LessonTabs,
  LessonVideo
} from '../../components'
import { courseFindMany } from '../../graphql'
import {
  setCourse,
  setCourseLoading,
  setLesson,
  setShowLessonSidebar,
  useAppDispatch,
  useAppSelector
} from '../../redux'
import { CourseType } from '../../types'
import {
  ROUTES,
  getCourseLessons,
  getLessonIndex,
  useCourse,
  useLesson,
  useSegment
} from '../../utils'
import { StyledLessonView } from '.'

const LessonView: FC = () => {
  const history = useHistory()
  const apollo = useApolloClient()
  const dispatch = useAppDispatch()
  const { checkForSkippedLessons } = useCourse()
  const { handleUnSkip } = useLesson()
  const { track } = useSegment()
  const { courseSlug, lessonSlug }: any = useParams()
  const { user } = useAppSelector((state) => state.user)
  const { loading, lesson, course } = useAppSelector((state) => state.course)

  const loadCourse = debounce(async () => {
    try {
      const { data } = await apollo.query({
        fetchPolicy: 'no-cache',
        query: courseFindMany,
        variables: { slug: courseSlug }
      })

      const course = data.courseFindMany.docs[0] as CourseType

      if (!course) {
        return history.push(ROUTES.home)
      }

      const lessons = getCourseLessons(course)
      const currLesson = lessons.find((l) => l.slug === lessonSlug) || lessons[0]

      if (!currLesson) {
        return history.push(ROUTES.home)
      }

      track('Loaded Lesson', {
        _id: currLesson._id,
        title: currLesson.title,
        slug: currLesson.slug,
        video_duration: currLesson?.video?.duration || 0,
        file_count: currLesson?.additionalFiles?.length || 0,
        module_id: currLesson.module,
        course_id: course._id,
        course_title: course.title
      })

      history.push(ROUTES.course.byLesson(courseSlug, currLesson.slug))

      dispatch(setCourse(course))
      dispatch(setLesson(currLesson))
      dispatch(setCourseLoading(false))

      await checkForSkippedLessons(currLesson)
      await handleUnSkip(currLesson)
    } catch (err) {
      toast.error((err as any)?.message)
      console.error(err)
    }
  }, 1000)

  useEffect(() => {
    if (!user?.id) {
      return history.push(ROUTES.home)
    }

    if (courseSlug !== course?.slug) {
      dispatch(setCourseLoading(true))
      dispatch(setShowLessonSidebar(undefined))
    }

    loadCourse()

    return () => {
      loadCourse.cancel()
    }
  }, [user, courseSlug, lessonSlug])

  return (
    <StyledLessonView $loading={loading}>
      <div className="lesson-content-container">
        <LessonVideo />
        <div className="lesson-content">
          <h1 className="lesson-title">{!loading && lesson?.title}</h1>
          <span className="course-title">{!loading && course?.title}</span>
          <LessonButton />
          <LessonTabs />
          <LessonAbout />
          <LessonResourceList />
        </div>
      </div>
      <LessonSidebarExpand />
      <LessonSidebar />
    </StyledLessonView>
  )
}

export default LessonView
